/**
 * Resolves descriptions for handlebars nodes loaded from template content.
 *
 * When initialContent is loaded, convertFromTemplate() creates nodes without
 * descriptions. This utility walks the document, finds those nodes, and uses
 * the autocomplete provider to resolve their descriptions — so they render
 * the same as tags inserted via autocomplete.
 */

import type { Editor } from '@tiptap/core';
import type {
  AutocompleteProvider,
  AutocompleteContext,
  BlockContextItem,
  HandlebarsBlockType,
  ResolveRequest,
  ResolveResponse,
} from '../types';
import { computeBlockContextAt } from '../extensions/handlebars/blockContext';

interface UnresolvedNode {
  pos: number;
  nodeType: 'handlebarsTag' | 'handlebarsBlock';
  variable: string;
  blockContext: BlockContextItem[];
  blockType?: HandlebarsBlockType;
}

/**
 * Collect all handlebars nodes in the document that lack descriptions.
 */
function collectUnresolvedNodes(editor: Editor): UnresolvedNode[] {
  const nodes: UnresolvedNode[] = [];

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'handlebarsTag' && node.attrs.variable) {
      // Collect if: no description at all, or has a multi-word variable with a
      // plain-text description that needs upgrading to token format
      const needsResolve = !node.attrs.description ||
        (node.attrs.variable.includes(' ') && node.attrs.description && !node.attrs.description.startsWith('[{'));
      if (needsResolve) {
        const blockContext = computeBlockContextAt(editor, pos);
        nodes.push({
          pos,
          nodeType: 'handlebarsTag',
          variable: node.attrs.variable,
          blockContext,
        });
      }
    }

    if (node.type.name === 'handlebarsBlock' && (!node.attrs.description || (node.attrs.variable && !node.attrs.variableDescription)) && (node.attrs.variable || node.attrs.blockType)) {
      const blockContext = computeBlockContextAt(editor, pos);
      nodes.push({
        pos,
        nodeType: 'handlebarsBlock',
        variable: node.attrs.variable,
        blockContext,
        blockType: node.attrs.blockType,
      });
    }
  });

  return nodes;
}

/**
 * Create a deduplication key from variable + block context + node type.
 */
function makeKey(variable: string, blockContext: BlockContextItem[], isBlock: boolean, blockType?: string): string {
  const contextKey = blockContext.map(b => `${b.type}:${b.variable}`).join('/');
  const blockPrefix = isBlock ? `block[${blockType || ''}]:` : '';
  return `${blockPrefix}${variable}@${contextKey}`;
}

/**
 * Deduplicate resolve requests: group nodes that share the same variable+context.
 */
function deduplicateRequests(nodes: UnresolvedNode[]): {
  requests: ResolveRequest[];
  keyToNodes: Map<string, UnresolvedNode[]>;
} {
  const keyToNodes = new Map<string, UnresolvedNode[]>();
  const requests: ResolveRequest[] = [];

  for (const node of nodes) {
    const isBlock = node.nodeType === 'handlebarsBlock';
    const key = makeKey(node.variable, node.blockContext, isBlock, node.blockType);

    if (!keyToNodes.has(key)) {
      keyToNodes.set(key, []);
      requests.push({
        variable: node.variable,
        blockContext: node.blockContext,
        isBlock,
        blockType: node.blockType,
      });
    }
    keyToNodes.get(key)!.push(node);
  }

  return { requests, keyToNodes };
}

/**
 * Resolve descriptions using onResolve (batch) or fall back to onQuery (per-variable).
 */
async function resolveViaProvider(
  provider: AutocompleteProvider,
  requests: ResolveRequest[],
  editor: Editor,
  allowFreeformHandlebars: boolean,
): Promise<Map<string, ResolveResponse>> {
  const resultMap = new Map<string, ResolveResponse>();

  // Prefer batch onResolve if available
  if (provider.onResolve) {
    try {
      const responses = await provider.onResolve(requests);
      for (let i = 0; i < responses.length; i++) {
        const response = responses[i];
        const req = requests[i];
        if (req) {
          const key = makeKey(req.variable, req.blockContext, req.isBlock, req.blockType);
          resultMap.set(key, response);
        }
      }
      return resultMap;
    } catch (err) {
      console.warn('onResolve failed, falling back to onQuery:', err);
    }
  }

  // Fallback: use onQuery for each unique variable
  const promises = requests.map(async (req) => {
    const key = makeKey(req.variable, req.blockContext, req.isBlock, req.blockType);
    try {
      const words = req.variable.trim().split(/\s+/).filter(Boolean);
      const currentWord = words[words.length - 1] || '';
      const previousWords = words.slice(0, -1);

      const context: AutocompleteContext = {
        fullText: req.variable,
        currentWord,
        previousWords,
        editor,
        blockContext: req.blockContext,
      };

      const response = await provider.onQuery(req.variable, context);

      if (response.isComplete && response.items.length > 0) {
        resultMap.set(key, {
          variable: req.variable,
          description: response.items[0].description || null,
          isValid: true,
        });
      } else if (response.items.length > 0) {
        // Find exact match in items
        const exactMatch = response.items.find(
          item => item.value === req.variable
        );
        if (exactMatch) {
          resultMap.set(key, {
            variable: req.variable,
            description: exactMatch.description || null,
            isValid: true,
          });
        }
      } else if (response.error && !allowFreeformHandlebars) {
        resultMap.set(key, {
          variable: req.variable,
          description: null,
          isValid: false,
          error: response.error,
        });
      }
    } catch {
      // Silently skip failed resolutions
    }
  });

  await Promise.all(promises);
  return resultMap;
}

/**
 * Resolve descriptions for all unresolved handlebars nodes in the editor.
 *
 * Walks the document tree, finds nodes without descriptions, resolves them
 * via the autocomplete provider, and updates the nodes in a single transaction.
 *
 * @param editor - The Tiptap editor instance
 * @param provider - The autocomplete provider
 * @param showDescriptions - Whether descriptions should be shown
 * @returns Number of nodes updated
 */
export async function resolveNodeDescriptions(
  editor: Editor,
  provider: AutocompleteProvider,
  showDescriptions: boolean,
  allowFreeformHandlebars = false,
): Promise<number> {
  if (!showDescriptions) {
    return 0;
  }

  const unresolvedNodes = collectUnresolvedNodes(editor);
  if (unresolvedNodes.length === 0) {
    return 0;
  }

  const { requests, keyToNodes } = deduplicateRequests(unresolvedNodes);
  const results = await resolveViaProvider(provider, requests, editor, allowFreeformHandlebars);

  // Apply results in a single transaction
  const { tr } = editor.state;
  let updatedCount = 0;

  for (const [key, response] of results) {
    const nodes = keyToNodes.get(key);
    if (!nodes) continue;

    for (const unresolvedNode of nodes) {
      const node = tr.doc.nodeAt(unresolvedNode.pos);
      if (!node) continue;
      if (node.type.name !== unresolvedNode.nodeType) continue;
      if (node.attrs.variable !== unresolvedNode.variable) continue;

      const newAttrs = { ...node.attrs };
      if (response.description) {
        newAttrs.description = response.description;
      }
      if (response.variableDescription) {
        newAttrs.variableDescription = response.variableDescription;
      }
      if (!response.isValid && !allowFreeformHandlebars) {
        newAttrs.isValid = false;
        newAttrs.error = response.error || null;
      }

      if (newAttrs.description !== node.attrs.description ||
          newAttrs.variableDescription !== node.attrs.variableDescription ||
          newAttrs.isValid !== node.attrs.isValid ||
          newAttrs.error !== node.attrs.error) {
        tr.setNodeMarkup(unresolvedNode.pos, undefined, newAttrs);
        updatedCount++;
      }
    }
  }

  if (updatedCount > 0) {
    tr.setMeta('resolveDescriptions', true);
    editor.view.dispatch(tr);
  }

  return updatedCount;
}
